import Protomux from 'protomux'
import c from 'compact-encoding'
import b4a from 'b4a'
import z32 from 'z32'
import {
  randomBytes, equal, toBoxPublicKey,
  encodePairRequest, decodePairRequest,
  encodePairResponse, decodePairResponse,
  encodePairWriter, decodePairWriter
} from '@pine/crypto'
import { ROLE } from '@pine/protocol'
import { Emitter } from './emitter.js'

const PROTOCOL = 'pine/pair/1'
const DEFAULT_TIMEOUT = 90000

const frame = { preencode: c.buffer.preencode, encode: c.buffer.encode, decode: c.buffer.decode }

/**
 * Why both sides call `mux.pair()` before opening their own channel.
 *
 * When one side opens a channel the other has not created yet, Protomux
 * rejects it — and does not replay the attempt, so the two never meet.
 * `pair()` is the hook for exactly that: it fires when the remote announces
 * the protocol, letting the late side open its half instead of refusing.
 *
 * On a fresh connection this never came up, because both sides attach in the
 * same tick. It bites when the two are already connected for some other
 * reason — starting a direct chat with somebody you share a group with, which
 * is the ordinary way anyone does it.
 */

/**
 * Protomux pauses a whole muxer while an `onmessage` handler's promise is
 * pending, so a handler that awaits anything slow stalls every other protocol
 * on that connection — replication included. Handlers here therefore hand the
 * real work off and return synchronously.
 */
function detach (fn, onError) {
  return (...args) => {
    Promise.resolve().then(() => fn(...args)).catch((err) => {
      if (onError) onError(err)
    })
  }
}

/**
 * Host side of an invite.
 *
 * Any member with invite rights can run this — the room's invites live in its
 * encrypted metadata, so pairing keeps working when the person who created the
 * invite is offline, as long as *someone* with rights is online. That is the
 * one hard requirement P2P cannot escape: a room with every member offline
 * cannot admit a new one.
 */
export class PairingHost extends Emitter {
  constructor (client, room, invite) {
    super()
    this.client = client
    this.room = room
    this.invite = invite
    this.topic = invite.id
    this.channels = new Set()
    this.muxes = new Set()
    this._detach = null
    this._pending = new Map() // nonce hex -> { account, name }
    this.started = false
  }

  async start () {
    if (this.started) return
    this.started = true

    this._detach = this.client.transport.onConnection((conn, info) => this._attach(conn, info))
    for (const conn of this.client.transport.connections) this._attach(conn, conn)

    this.client.transport.join(this.topic, { server: true, client: true })
    // Announce in the background rather than blocking the caller on a DHT
    // round trip. Starting a chat mints an invite and starts a host, and the
    // person doing it should not wait ~15s staring at the network for that;
    // the guest side retries its lookup until this lands. Awaiting it here was
    // what made creating a direct chat feel like it hung.
    this.client.transport.announced(this.topic)
      .then(() => this.emit('listening'))
      .catch(() => {})
  }

  async stop () {
    if (!this.started) return
    this.started = false
    if (this._detach) this._detach()
    this._unpair()
    for (const ch of this.channels) {
      try { ch.close() } catch {}
    }
    this.channels.clear()
    await this.client.transport.leave(this.topic).catch(() => {})
  }

  _attach (stream) {
    const mux = Protomux.from(stream)
    this.muxes.add(mux)
    mux.pair({ protocol: PROTOCOL, id: this.topic }, () => this._open(mux))
    this._open(mux)
  }

  _unpair () {
    for (const mux of this.muxes) {
      try { mux.unpair({ protocol: PROTOCOL, id: this.topic }) } catch {}
    }
    this.muxes.clear()
  }

  _open (mux) {
    const channel = mux.createChannel({
      protocol: PROTOCOL,
      id: this.topic,
      onclose: () => this.channels.delete(channel)
    })
    if (!channel) return // already open on this connection

    const onError = (err) => this.emit('error', err)
    const request = channel.addMessage({
      encoding: frame,
      onmessage: detach((buf) => this._onRequest(buf, response), onError)
    })
    const response = channel.addMessage({ encoding: frame })
    const writer = channel.addMessage({
      encoding: frame,
      onmessage: detach((buf) => this._onWriter(buf), onError)
    })
    void request
    void writer

    channel.open()
    this.channels.add(channel)
  }

  async _onRequest (buf, response) {
    if (this.invite.expired) {
      this.emit('rejected', { reason: 'expired' })
      return
    }

    const req = decodePairRequest(buf, this.invite.requestKey)
    if (!req) return // not for this invite, or a probe

    const account = z32.encode(req.account)
    this.emit('request', { account, name: req.name })

    /*
     * Somebody already in the room is asking again.
     *
     * That is a reinstall, or a second device on the same account, and it
     * still needs a writer slot: the core it writes from is per-device, so the
     * one recorded when they first joined is not the one they now hold. The
     * keys used to come back without the nonce being registered, so the writer
     * message that followed was dropped as unknown — the join "worked" only
     * after the guest gave up waiting for write access, forty-five seconds
     * later, and landed read-only. Membership is not re-granted by this: the
     * fold keeps the role the room already recorded for them.
     */
    this._pending.set(b4a.toString(req.nonce, 'hex'), { account: req.account, name: req.name })
    await this._respond(response, req)
  }

  async _respond (response, req) {
    const meta = await this.room.view.meta().catch(() => null)
    const payload = encodePairResponse({
      roomKey: this.room.key,
      encryptionKey: this.room.encryptionKey,
      nonce: req.nonce,
      meta: JSON.stringify({
        name: (meta && meta.name) || this.invite.hint || '',
        type: meta ? meta.type : 1
      }),
      inviter: this.client.identity.publicKey
    }, this.invite.responseKey, toBoxPublicKey(req.account))

    try { response.send(payload) } catch { /* peer vanished */ }
  }

  async _onWriter (buf) {
    const msg = decodePairWriter(buf, this.invite.requestKey)
    if (!msg) return

    const nonceHex = b4a.toString(msg.nonce, 'hex')
    const pending = this._pending.get(nonceHex)
    if (!pending) return
    if (!equal(pending.account, msg.account)) return // nonce replay with a different account
    this._pending.delete(nonceHex)

    try {
      await this.room.addWriter(msg.writerKey, msg.account, {
        name: pending.name,
        role: this.invite.role === 'admin' ? ROLE.ADMIN : ROLE.MEMBER
      })
      this.invite.used++
      this.emit('joined', { account: z32.encode(msg.account), name: pending.name })
      if (this.invite.expired) await this.stop()
    } catch (err) {
      this.emit('error', err)
    }
  }
}

/**
 * Guest side. Resolves once we hold the room key *and* our writer core has
 * been authorised, so the caller can start posting immediately.
 */
export class PairingGuest extends Emitter {
  constructor (client, invite) {
    super()
    this.client = client
    this.invite = invite
    this.topic = invite.id
    this.nonce = randomBytes(32)
    this.channels = new Set()
    this.muxes = new Set()
    this._detach = null
    this._settled = false
    this._room = null
    this._retry = null
    this._writerSent = new Set()
  }

  async pair ({ timeout = DEFAULT_TIMEOUT } = {}) {
    return new Promise((resolve, reject) => {
      const done = (err, room) => {
        if (this._settled) return
        this._settled = true
        clearTimeout(timer)
        this._teardown()
        if (err) reject(err)
        else resolve(room)
      }

      const timer = setTimeout(() => {
        done(new Error('No one is online to let you in. Ask someone in the room to open Pine and try again.'))
      }, timeout)

      this.once('paired', (room) => done(null, room))
      this.once('failed', (err) => done(err))

      this._detach = this.client.transport.onConnection((conn, info) => this._attach(conn, info))
      for (const conn of this.client.transport.connections) this._attach(conn, conn)
      this.client.transport.join(this.topic, { server: false, client: true })

      // Hyperswarm backs off between lookups; a joiner who is actively waiting
      // should keep asking rather than sit through the long retry cycle.
      this._retry = setInterval(() => {
        if (this._settled) return
        this.client.transport.refresh(this.topic)
        for (const conn of this.client.transport.connections) this._attach(conn, conn)
      }, 3000)
      if (this._retry.unref) this._retry.unref()
    })
  }

  _teardown () {
    if (this._retry) clearInterval(this._retry)
    if (this._detach) this._detach()
    this._unpair()
    for (const ch of this.channels) {
      try { ch.close() } catch {}
    }
    this.channels.clear()
    this.client.transport.leave(this.topic).catch(() => {})
  }

  _attach (stream) {
    const mux = Protomux.from(stream)
    this.muxes.add(mux)
    mux.pair({ protocol: PROTOCOL, id: this.topic }, () => this._open(mux))
    this._open(mux)
  }

  _unpair () {
    for (const mux of this.muxes) {
      try { mux.unpair({ protocol: PROTOCOL, id: this.topic }) } catch {}
    }
    this.muxes.clear()
  }

  _open (mux) {
    const channel = mux.createChannel({
      protocol: PROTOCOL,
      id: this.topic,
      onclose: () => this.channels.delete(channel)
    })
    if (!channel) return // already open on this connection

    const request = channel.addMessage({ encoding: frame })
    const response = channel.addMessage({
      encoding: frame,
      onmessage: detach((buf) => this._onResponse(buf, writer), (err) => this.emit('failed', err))
    })
    const writer = channel.addMessage({ encoding: frame })
    void response

    channel.open()
    this.channels.add(channel)

    const payload = encodePairRequest({
      account: this.client.identity.publicKey,
      name: this.client.identity.profile.name || '',
      nonce: this.nonce
    }, this.invite.requestKey)

    try { request.send(payload) } catch { /* peer vanished */ }
  }

  async _onResponse (buf, writer) {
    if (this._settled) return

    const res = decodePairResponse(
      buf,
      this.invite.responseKey,
      this.client.identity.boxPublicKey,
      this.client.identity.boxSecretKey
    )
    if (!res) return
    if (!equal(res.nonce, this.nonce)) return // response to somebody else's request

    try {
      if (!this._room) {
        this._room = await this.client._materialiseRoom({
          key: res.roomKey,
          encryptionKey: res.encryptionKey,
          meta: safeJSON(res.meta)
        })
      }

      const writerKey = this._room.writerKey
      const keyHex = b4a.toString(writerKey, 'hex')
      if (!this._writerSent.has(keyHex)) {
        this._writerSent.add(keyHex)
      }

      const payload = encodePairWriter({
        account: this.client.identity.publicKey,
        writerKey,
        nonce: this.nonce
      }, this.invite.requestKey)
      try { writer.send(payload) } catch {}

      const ok = await this._room.waitForWritable({ timeout: 45000 })
      if (!ok) {
        // We hold the keys and can read the room, we just cannot post yet.
        this.emit('paired', this._room)
        return
      }
      await this._room.announceSelf()
      this.emit('paired', this._room)
    } catch (err) {
      this.emit('failed', err)
    }
  }
}

function safeJSON (str) {
  try { return JSON.parse(str) } catch { return {} }
}
